import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";

import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import { parseLeadsSearchParams } from "common/helpers/utils";
import type { Lead, LeadsSearchParams, SelectedFacetParam } from "common/types";

async function getDripLeadsRequest(
  workspaceId: string,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
  options: {
    pageSize: number;
    pageParam: number;
  },
): Promise<Lead[]> {
  const { pageSize, pageParam } = options;

  const queryParams = new URLSearchParams();
  queryParams.set("start", (pageParam * pageSize).toString());
  queryParams.set("count", pageSize.toString());

  const { leads } = await post(
    `workspaces/${workspaceId}/leads-search?${queryParams.toString()}`,
    parseLeadsSearchParams(searchParams),
  );

  return leads;
}

export default function useDripLeads(options: {
  searchParams: LeadsSearchParams<SelectedFacetParam>;
  pageSize: number;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { searchParams, pageSize } = options;

  const { leadsKeys } = getQueryKeys(workspaceId);

  const {
    data,
    hasNextPage: hasMoreDripLeads,
    isFetchingNextPage: isFetchingMoreLeads,
    fetchNextPage: fetchMoreLeads,
    isError: isDripLeadsError,
    refetch: refetchDripLeads,
    isFetching: isFetchingLeads,
  } = useInfiniteQuery({
    queryKey: leadsKeys.list(searchParams, { pageSize }),
    queryFn: ({ pageParam }) =>
      getDripLeadsRequest(workspaceId, searchParams, {
        pageSize,
        pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !Array.isArray(lastPage) || lastPage.length === 0) {
        return undefined;
      }
      return lastPage.length > 0 ? allPages.length : undefined;
    },
    placeholderData: keepPreviousData,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  return {
    leads: data?.pages.flat(),
    isDripLeadsError,
    refetchDripLeads,
    hasMoreDripLeads,
    isFetchingMoreLeads,
    fetchMoreLeads,
    isFetchingLeads,
  };
}
